import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, User, Mail, Calendar, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';
import PageLoadingState from '../components/PageLoadingState';

export default function AdminEncashment() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getEncashmentRequests(
        statusFilter !== 'all' ? { status: statusFilter } : {}
      );
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch encashment requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleUpdateStatus = async (requestId, status, reason = '') => {
    try {
      setIsProcessing(true);
      await adminApi.updateEncashmentStatus(requestId, { status, reason });
      toast.success(`Request ${status} successfully`);
      fetchRequests();
      setIsRejectDialogOpen(false);
      setRejectionReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${status} request`);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'rejected': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">
              Encashment Requests
            </h1>
            <p className="text-zinc-400 mt-1">Review and manage user credit encashment requests</p>
          </div>
          
          <div className="w-full md:w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <PageLoadingState title="Loading encashment requests" className="min-h-[20rem] pt-0" />
        ) : requests.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900/50 rounded-2xl border border-zinc-800">
            <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-zinc-300">No requests found</h3>
            <p className="text-zinc-500 mt-2">There are no encashment requests to display.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {requests.map((request) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 hover:bg-zinc-900/80 transition-all duration-300"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                        <User className="text-purple-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-200">{request.user_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                          <Mail className="w-3 h-3" />
                          {request.user_email}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 md:gap-8">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Amount</p>
                        <p className="text-xl font-bold text-white">₹{request.amount}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Date</p>
                        <div className="flex items-center gap-2 text-zinc-300">
                          <Calendar className="w-4 h-4 text-zinc-500" />
                          {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Status</p>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)} capitalize`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        className="border-red-500/20 text-red-500 hover:bg-red-500/10"
                        onClick={() => {
                          setSelectedRequest(request);
                          setIsRejectDialogOpen(true);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleUpdateStatus(request.id, 'approved')}
                        disabled={isProcessing}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                    </div>
                  )}

                  {request.status !== 'pending' && (
                    <div className="text-sm text-zinc-500 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Processed on {new Date(request.processed_at).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {request.rejection_reason && (
                  <div className="mt-4 p-3 bg-red-500/5 border border-red-500/10 rounded-lg flex gap-2 items-start">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-red-500 uppercase tracking-wider">Rejection Reason</p>
                      <p className="text-sm text-zinc-400">{request.rejection_reason}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Reject Encashment Request</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Please provide a reason for rejecting this request. This will be sent to the user via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for rejection</Label>
              <Input
                id="reason"
                placeholder="e.g., Suspicious activity, minimum balance not maintained..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleUpdateStatus(selectedRequest?.id, 'rejected', rejectionReason)}
              disabled={!rejectionReason || isProcessing}
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
